import assign from 'lodash/assign';
import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';
import reduce from 'lodash/reduce';
import toPlainObject from 'lodash/toPlainObject';
import omit from 'lodash/omit';
import type { FilterType } from '../models/Types';

// Misc / Utils
import { capitalizeFirstLetter } from './stringUtils';
import Filter from '../models/Filter';

// Types
type objectType = {
  [key: string]: string | Array<string> | number | objectType;
};
export type filterLabelsType = {
  allFiltersLabel: string;
  activeRentalsLabel: string;
  offMarketLabel: string;
  bedroomsLabel: string;
  propertyTypesLabel: string;
  distanceLabel: string;
  amenitiesLabel: string;
};
export type PotentialFilterTypes = {
  activeTypes?: Array<string>;
  activatedDays?: number;
  deactivatedDays?: number;
  propertyTypes?: Array<string>;
  listingTypes?: Array<string>;
  bedrooms?: Array<string>;
  pets?: Array<string>;
  laundry?: Array<string>;
  amenities?: Array<string>;
  distanceInMiles?: number;
  price?: number;
};

const _defaultFilterLabels = {
  allFiltersLabel: 'Filters',
  activeRentalsLabel: 'Active rentals',
  offMarketLabel: 'Off Market',
  bedroomsLabel: 'All beds',
  propertyTypesLabel: 'All home types',
  distanceLabel: 'Any distance',
  amenitiesLabel: 'All amenities',
};

export function partialFilterToFilterParams(_filterObj: objectType) {
  /**
   * partialFilterToFilterParams returns two filter objects - one
   * comma-separated (for GraphQL) and one hyphen-separated (for
   * routing)
   */
  const filterObj = toPlainObject(_filterObj);

  const createFilterParams = (delimeter: '-' | ',') => {
    return reduce(
      filterObj,
      (accum, value, key) => {
        if (isObject(value)) {
          const truthyKeys = Object.keys(value)
            // @ts-ignore
            .filter((key) => value[key]) as Array<any>;

          // deselecting all options will return 'none' (RG-247)
          accum[key] = truthyKeys.length ? truthyKeys.join(delimeter) : 'none';
        } else {
          accum[key] = value;
        }
        return accum;
      },
      {} as objectType,
    );
  };

  const filterForGraphQL = createFilterParams(',');
  const filterForUrl = createFilterParams('-');
  // delete default, truthy activeTypes from url (graphQL always requires activeTypes)
  if (filterForUrl.activeTypes === 'active-inactive') {
    delete filterForUrl.activeTypes;
  }

  return {
    filterForGraphQL,
    filterForUrl,
  };
}

export function getFilterDiff({
  baseFilter,
  editedFilter,
  excludeFilters = [],
}: {
  baseFilter: FilterType;
  editedFilter: FilterType;
  excludeFilters?: Array<string>;
}) {
  const filterDiff = {} as objectType;
  const filterKeys = omit(baseFilter, excludeFilters);
  for (const key in filterKeys) {
    if (!isEqual(baseFilter[key], editedFilter[key])) {
      if (isObject(baseFilter[key]) && isObject(editedFilter[key])) {
        filterDiff[key] = getFilterDiff({
          baseFilter: baseFilter[key] as FilterType,
          editedFilter: editedFilter[key] as FilterType,
        });
      } else {
        filterDiff[key] = editedFilter[key];
      }
    }
  }
  return filterDiff;
}

export function getActiveFilters(filter: FilterType): PotentialFilterTypes {
  const defaultFilter = toPlainObject(new Filter());
  const activeFilters = getFilterDiff({
    baseFilter: defaultFilter,
    editedFilter: filter,
  });

  Object.entries(activeFilters).forEach(([k, v]) => {
    if (isObject(v)) {
      const fullFilter = assign({}, defaultFilter[k], activeFilters[k]);
      activeFilters[k] = Object.keys(fullFilter).filter(
        (opt) => fullFilter[opt], // keep truthy options only
      );
    }
  });

  return activeFilters;
}

export function getFilterLabels(currFilter: FilterType): filterLabelsType {
  const activeFilters = getActiveFilters(currFilter);

  /**
   * note: numFiltersSet !== Object.keys(filterDiff).length
   * ex: numFiltersSet counts `cats` and `dogs` independently
   */
  let numFiltersSet = 0;
  const filterLabels = { ..._defaultFilterLabels } as filterLabelsType;
  let amenitiesFilter = [] as Array<string>;

  if (!activeFilters.activeTypes || (activeFilters.activeTypes && activeFilters.activeTypes.includes('active'))) {
    if (activeFilters.activatedDays) {
      filterLabels.activeRentalsLabel = `Active rentals ${activeFilters.activatedDays} days`;
      numFiltersSet++;
    } else {
      filterLabels.activeRentalsLabel = 'Active rentals';
    }
  } else {
    filterLabels.activeRentalsLabel = 'Hiding active rentals';
    numFiltersSet++;
  }

  if (!activeFilters.activeTypes || (activeFilters.activeTypes && activeFilters.activeTypes.includes('inactive'))) {
    if (activeFilters.deactivatedDays && activeFilters.deactivatedDays !== 30) {
      filterLabels.offMarketLabel = `Off market ${activeFilters.deactivatedDays} days`;
      numFiltersSet++;
    } else {
      filterLabels.offMarketLabel = 'Off market';
    }
  } else {
    filterLabels.offMarketLabel = 'Hiding off market';
    numFiltersSet++;
  }

  if (activeFilters.bedrooms) {
    if (activeFilters.bedrooms[0] === '0') {
      activeFilters.bedrooms[0] = 'Studio';
    }
    filterLabels.bedroomsLabel = activeFilters.bedrooms.join(', ');

    if (filterLabels.bedroomsLabel === 'Studio') {
      filterLabels.bedroomsLabel += 's';
    } else {
      filterLabels.bedroomsLabel += ' beds';
    }
    filterLabels.bedroomsLabel = filterLabels.bedroomsLabel.split('plus').join('+');
    numFiltersSet++;
  }
  if (activeFilters.propertyTypes) {
    filterLabels.propertyTypesLabel =
      activeFilters.propertyTypes.length === 1
        ? activeFilters.propertyTypes[0] === 'townhouse'
          ? 'Townhomes'
          : capitalizeFirstLetter(activeFilters.propertyTypes[0]) + 's'
        : `${activeFilters.propertyTypes.length} home types`;
    numFiltersSet++;
  }
  if (activeFilters.distanceInMiles) {
    filterLabels.distanceLabel = `Within ${currFilter.distanceInMiles} mi`;
    numFiltersSet++;
  }
  if (activeFilters.pets) {
    amenitiesFilter = amenitiesFilter.concat(activeFilters.pets);
  }
  if (activeFilters.laundry) {
    amenitiesFilter = amenitiesFilter.concat(activeFilters.laundry);
  }
  if (activeFilters.amenities) {
    amenitiesFilter = amenitiesFilter.concat(activeFilters.amenities);
  }
  if (amenitiesFilter.length) {
    filterLabels.amenitiesLabel = `${amenitiesFilter.length} ${amenitiesFilter.length === 1 ? 'amenity' : 'amenities'}`;
  }
  numFiltersSet += amenitiesFilter.length;

  filterLabels.allFiltersLabel = numFiltersSet
    ? `${numFiltersSet} ${numFiltersSet === 1 ? 'filter' : 'filters'} set`
    : 'Filters';

  return filterLabels;
}
